import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'
import { evaluateFormula } from './formulaEval'

/**
 * ReactiveJSON primitive — affiche le résultat d'une petite formule arithmétique
 * calculée sur des valeurs du store.
 *
 * POURQUOI UNE PRIMITIVE (échappatoire config-first, justifiée) : ni le cœur de
 * reactive-json ni les plugins Orbit ne CALCULENT une valeur à partir de plusieurs
 * données (`setData` n'écrit qu'un littéral ou la valeur d'un événement,
 * `FormatNumeral` ne fait que formater). Un calculateur de quantités (cartouches
 * de mastic, kilos de joint…) est pourtant du contenu ordinaire : la formule et
 * ses entrées restent dans le YAML, seule l'évaluation est ici.
 *
 * L'évaluation (sans `eval`, cf. formulaEval.js) ne connaît que les nombres, les
 * variables déclarées, `+ - * /`, les parenthèses et `ceil floor round min max abs`.
 * Une variable absente, une division par zéro ou une formule invalide rendent
 * `empty`, jamais une exception.
 *
 *   - type: Formula
 *     expression: "ceil(w * d * l * 1.1 / 310)"
 *     vars: { w: ~~.width, d: ~~.depth, l: ~~.length }   # chaque valeur est résolue
 *     decimals: 0             # chiffres après la virgule (défaut : 0)
 *     locale: en-GB           # format du nombre (défaut : en-GB)
 *     empty: "—"              # rendu si le calcul n'aboutit pas (défaut : "—")
 */

export const Formula = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const expression = resolve(props.expression)
  // Intl.NumberFormat n'accepte que 0 à 20 décimales.
  const decimals = Math.min(20, Math.max(0, Math.trunc(Number(resolve(props.decimals)) || 0)))
  const locale = resolve(props.locale) || 'en-GB'
  const empty = resolve(props.empty) ?? '—'
  // Un objet en prop n'est pas résolu par le moteur : chaque entrée l'est ici.
  const vars = Object.fromEntries(Object.entries(props.vars ?? {}).map(([k, v]) => [k, resolve(v)]))

  let text = empty
  try {
    const n = evaluateFormula(expression, vars)
    if (Number.isFinite(n)) {
      text = new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n)
    }
  }
  catch {
    text = empty
  }

  return (
    <ActionDependant {...props}>
      {text}
    </ActionDependant>
  )
}
